import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import ConfirmationDialog from '../../../components/dialogs/ConfirmationDialog';
import NutritionPDFDocument from './NutritionPDF';
import type { NutritionPdfData } from './types';

interface NutritionPDFExportButtonProps {
    /** Built lazily so the (fairly heavy) snapshot is only produced on export. */
    buildData: (patientInfo: string) => NutritionPdfData;
    fileNamePrefix: string;
}

export const NutritionPDFExportButton: React.FC<NutritionPDFExportButtonProps> = ({
    buildData,
    fileNamePrefix,
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [patientInfo, setPatientInfo] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    const closeDialog = () => {
        setIsDialogOpen(false);
        setPatientInfo('');
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const data = buildData(patientInfo.trim());
            const blob = await pdf(<NutritionPDFDocument data={data} />).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const safePatientInfo = patientInfo.trim().replace(/[\\/\s]+/g, '_');
            const dateStr = new Date().toISOString().split('T')[0];
            link.download = safePatientInfo
                ? `${fileNamePrefix}-${safePatientInfo}-${dateStr}.pdf`
                : `${fileNamePrefix}-${dateStr}.pdf`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            closeDialog();
        } catch (error) {
            console.error('PDF generation error:', error);
            alert('שגיאה ביצירת קובץ PDF. אנא נסה שנית.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className="pdf-export-btn"
                data-testid="export-pdf-button"
                onClick={() => setIsDialogOpen(true)}
            >
                ייצוא ל PDF
            </button>

            <ConfirmationDialog
                open={isDialogOpen}
                onClose={closeDialog}
                onCancel={closeDialog}
                onConfirm={handleExport}
                title="ייצוא ל-PDF"
                confirmText={isExporting ? 'מייצא...' : 'ייצא ל-PDF'}
                cancelText="ביטול"
                isConfirmLoading={isExporting}
                confirmLoadingText="מייצא…"
            >
                <input
                    type="text"
                    data-testid="export-pdf-patient-info"
                    data-clarity-mask="true"
                    value={patientInfo}
                    onChange={(e) => setPatientInfo(e.target.value)}
                    placeholder="שם המטופל \ מיטה \ חדר"
                    disabled={isExporting}
                    style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '1rem',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        textAlign: 'right',
                        direction: 'rtl',
                    }}
                    autoFocus
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isExporting) {
                            handleExport();
                        }
                    }}
                />
            </ConfirmationDialog>
        </>
    );
};

export default NutritionPDFExportButton;
